/**
 * Clever roster-source lock (v3.64.0).
 *
 * Teachers whose class/student data is synced from Clever must not edit
 * rosters in-app — roster truth lives in the district SIS (e.g. Infinite
 * Campus → Clever), and any in-app edit is overwritten at the next sync
 * (and rules-denied as of v3.64.0). The gate keys off the DATA's source
 * (`source: 'clever'` / `cleverId` on the class or student doc), never the
 * login method: hybrid logins exist (the same teacher may sign in via
 * Google), so protection must follow the docs.
 *
 * Three layers share this module:
 *   - SidePanel greys the Classes & Students nav entry when ALL of the
 *     teacher's active-year classes are Clever-sourced (getRosterLock —
 *     one cached classes query per session, recorded in PERFORMANCE.md).
 *   - StudentUpload computes the same flags from the classes it already
 *     loaded (computeRosterLock — zero extra reads) and renders
 *     Clever-sourced classes read-only.
 *   - The guidance message names the district's SIS when the admin has set
 *     it on the district registry row (districts/{id}.sisName — one cached
 *     doc read, fetched only when the lock UI actually renders).
 */

import { doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { buildMyClassesQuery } from './classroomQueries';
import { isDocInSchoolYear } from './schoolYear';
import { resolveActiveSchoolYear } from './activeSchoolYear';

/**
 * Is this class/student doc synced from Clever? Same definition as
 * studentEdit.isSyncedRoster (the importer stamps both fields; a
 * hand-linked doc may carry only cleverId) — kept here so class-only
 * callers don't need a student arg.
 */
export function isCleverRosterDoc(data = null) {
  return !!data && (data.source === 'clever' || data.cleverId != null);
}

/**
 * Pure lock computation from already-loaded active-year class docs.
 * `classDatas` is an array of class doc data (StudentUpload passes
 * Object.values(classesMap)).
 *
 * allClever is the nav/page hard lock: the teacher has classes and every
 * one of them is Clever-sourced. anyClever drives the mixed-case UI.
 */
export function computeRosterLock(classDatas = []) {
  const list = classDatas.filter(Boolean);
  const cleverCount = list.filter(isCleverRosterDoc).length;
  return {
    hasClasses: list.length > 0,
    anyClever: cleverCount > 0,
    allClever: list.length > 0 && cleverCount === list.length,
  };
}

/**
 * The teacher-facing guidance line (Kemal, 2026-08-20). The SIS name is a
 * per-district registry field (districts/{id}.sisName) so nothing here
 * hardcodes Infinite Campus; districts that haven't set it get the
 * generic wording.
 */
export function rosterGuidanceText(sisName = null) {
  const sis = (typeof sisName === 'string' && sisName.trim())
    ? sisName.trim()
    : 'your student information system';
  return 'Your classes and students are managed by your district and sync '
    + 'automatically from Clever. Need a change? Ask your school office to '
    + `update it in ${sis}.`;
}

const classesCache = new Map(); // uid → Promise<live class docs>
const sisNameCache = new Map(); // districtId → Promise<string|null>

/**
 * Session-cached LIVE class docs for the signed-in teacher/EA: one classes
 * query (the same buildMyClassesQuery shape every roster page issues),
 * filtered to non-deleted active-year docs — `[{ id, ...data }]`, empty when
 * no school year is active. Rosters change rarely mid-session — same caching
 * stance as the Academic Calendar (activeSchoolYear.js). A failed load is
 * evicted (not cached) and REJECTS; callers choose their safe default.
 * v3.80.0: split out of getRosterLock so the Grade Team probe
 * (gradeTeamSource.js) reads the same one query instead of issuing a second.
 */
export function getMyLiveClasses(user) {
  const uid = user?.uid;
  if (!uid) return Promise.resolve([]);
  if (!classesCache.has(uid)) {
    const pending = (async () => {
      const sy = await resolveActiveSchoolYear(user?.profile?.districtId || null);
      if (sy.status !== 'active') return [];
      const snap = await getDocs(buildMyClassesQuery(user));
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => !c.deleted && isDocInSchoolYear(c, sy.startYear));
    })().catch((err) => {
      classesCache.delete(uid);
      throw err;
    });
    classesCache.set(uid, pending);
  }
  return classesCache.get(uid);
}

/**
 * Session-cached roster lock for the signed-in teacher/EA, computed from the
 * cached live classes above. A failed load reports the safe default: nothing
 * locked, so the nav stays fully enabled rather than wrongly grey.
 */
export function getRosterLock(user) {
  if (!user?.uid) return Promise.resolve(computeRosterLock([]));
  return getMyLiveClasses(user).then(computeRosterLock).catch((err) => {
    console.error('getRosterLock: classes load failed — leaving nav unlocked', err);
    return computeRosterLock([]);
  });
}

/**
 * The district's SIS display name (districts/{id}.sisName), cached for the
 * session. Only called when lock UI is actually rendered. Any failure or
 * missing field resolves null → generic wording.
 */
export function getDistrictSisName(districtId) {
  if (!districtId) return Promise.resolve(null);
  if (!sisNameCache.has(districtId)) {
    const pending = getDoc(doc(db, 'districts', districtId))
      .then((snap) => {
        const name = snap.exists() ? snap.data().sisName : null;
        return (typeof name === 'string' && name.trim()) ? name.trim() : null;
      })
      .catch(() => {
        sisNameCache.delete(districtId);
        return null;
      });
    sisNameCache.set(districtId, pending);
  }
  return sisNameCache.get(districtId);
}

/** Sign-out / test hook: clear the per-session caches (App.js handleLogout, v3.80.0). */
export function clearRosterSourceCache() {
  classesCache.clear();
  sisNameCache.clear();
}
